import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Tabs, Tab, Chip, CircularProgress } from '@mui/material';
import { BarChart3 } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import {} from '@lingaai/shared-hooks';
import { useCurrentUser } from '@lingaai/user-management';
import { MODULE_REGISTRY } from '@lingaai/types/graph/modules';
import { ICON_SIZES, ICON_STROKE } from '../constants/icons';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface Dashboard {
  id: string;
  name: string;
  description: string | null;
  is_shared: boolean;
  created_at: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export function Analytics() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const moduleFilter = searchParams.get('module');
  const { data: user } = useCurrentUser();
  const [tabValue, setTabValue] = useState(0);
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [loading, setLoading] = useState(true);

  const enabledModules = useMemo(() => {
    if (!user?.tenants?.module_config) return [];
    const moduleConfig = user.tenants.module_config as Record<string, { enabled: boolean }>;
    const enabledIds = Object.entries(moduleConfig)
      .filter(([, config]) => config.enabled)
      .map(([id]) => id)
      .filter((id) => id !== 'pmn');
    return enabledIds.map((id) => MODULE_REGISTRY[id]).filter((m): m is NonNullable<typeof m> => m !== undefined);
  }, [user]);

  // Fetch real dashboards from API
  useEffect(() => {
    if (!user?.tenant_id || !user?.id) return;

    const fetchDashboards = async () => {
      try {
        let url = `${API_URL}/dashboards?tenant_id=${user.tenant_id}&user_id=${user.id}`;
        if (moduleFilter) {
          url += `&module=${moduleFilter}`;
        }
        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          setDashboards(data.dashboards || []);
        }
      } catch (error) {
        console.error('Failed to fetch dashboards:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboards();
  }, [user?.tenant_id, user?.id, moduleFilter]);

  // Mock reports - keep for now
  const mockReports = [
    { id: '1', name: 'Pipeline Overview', moduleId: 'pevc-dealflow', type: 'report' },
    { id: '2', name: 'Deal Conversion Rates', moduleId: 'pevc-dealflow', type: 'report' },
    { id: '3', name: 'Portfolio Performance', moduleId: 'pevc-portfolio', type: 'report' },
    { id: '4', name: 'NAV Summary', moduleId: 'pevc-portfolio', type: 'report' },
  ];

  // Filter reports based on enabled modules
  const enabledModuleIds = enabledModules.map((m) => m.id);
  const availableReports = mockReports.filter((report) => report.moduleId === null || enabledModuleIds.includes(report.moduleId));

  // Apply module filter if present
  const reports = moduleFilter ? availableReports.filter((report) => report.moduleId === moduleFilter) : availableReports;

  const getModuleName = (moduleId: string | null): string => {
    if (!moduleId) return 'Cross-Module';
    const module = enabledModules.find((m) => m.id === moduleId);
    return module?.name || moduleId;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', color: '#6366f1' }}>
            <BarChart3 size={ICON_SIZES.xl} strokeWidth={ICON_STROKE} />
          </Box>
          <Typography variant="h4">Analytics</Typography>
        </Box>
        {moduleFilter && <Chip label={getModuleName(moduleFilter)} onDelete={() => (window.location.href = '/analytics')} color="primary" variant="outlined" />}
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab label={`Dashboards (${dashboards.length})`} />
          <Tab label={`Reports (${reports.length})`} />
        </Tabs>
      </Paper>

      <TabPanel value={tabValue} index={0}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : dashboards.length === 0 ? (
          <Typography color="text.secondary">No dashboards yet. Create one by saving a chart from the AI assistant.</Typography>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: 2,
            }}
          >
            {dashboards.map((dashboard) => (
              <Paper key={dashboard.id} onClick={() => navigate(`/analytics/dashboards/${dashboard.id}`)} sx={{ p: 3, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
                <Typography variant="h6">{dashboard.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {dashboard.description || 'No description'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {new Date(dashboard.created_at).toLocaleDateString()}
                  {dashboard.is_shared && ' • Shared'}
                </Typography>
              </Paper>
            ))}
          </Box>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {reports.length === 0 ? (
          <Typography color="text.secondary">No reports available</Typography>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: 2,
            }}
          >
            {reports.map((report) => (
              <Paper key={report.id} sx={{ p: 3, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
                <Typography variant="h6">{report.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {getModuleName(report.moduleId)}
                </Typography>
              </Paper>
            ))}
          </Box>
        )}
      </TabPanel>
    </Box>
  );
}
